// Caret preservation for controlled inputs whose value is reformatted on change
// (thousand separators, tax-number dashes, stripping invalid characters, ...).
// A plain controlled input resets the caret to the end whenever React re-sets
// node.value, so mid-string edits are lost. These helpers anchor the caret to
// the same digit the user was editing, independent of how grouping punctuation
// shifts. See the `preserveCaret` prop on Field.

// Characters a user "owns" in a formatted numeric value: digits, the decimal
// comma, and — in leading position only — a minus sign, both as the user types
// it (ASCII '-') and as formatAmount re-emits it (U+2212 via
// toLocaleString('sv-SE')), so the anchor agrees across that rewrite. Anywhere
// past index 0 a dash is grouping punctuation (org-number '556036-0793') and is
// ignored, like spaces, dots and rejected characters, so the caret stays
// anchored to the same digit no matter how the separators shift when the value
// is reformatted.
export const isValueChar = (ch: string, index: number): boolean =>
  /[\d,]/.test(ch) || (index === 0 && /[-−]/.test(ch));

/** Count the value characters (digits / decimal comma / leading minus) in a string. */
export const countValueChars = (s: string): number => {
  let n = 0;
  for (let i = 0; i < s.length; i++) if (isValueChar(s[i], i)) n++;
  return n;
};

/**
 * Position the caret just after the `anchor`-th value character in `value`.
 * `anchor` is the number of value characters that were to the left of the caret
 * before the value was reformatted (see countValueChars). Returns 0 when
 * `anchor` is <= 0, and the end of the string when there are fewer than `anchor`
 * value characters (e.g. after truncation).
 */
export const computeCaretPosition = (value: string, anchor: number): number => {
  if (anchor <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < value.length; i++) {
    if (isValueChar(value[i], i) && ++seen === anchor) return i + 1;
  }
  return value.length;
};
